import { Link } from 'expo-router';
import ArrowRight from 'lucide-react-native/icons/arrow-right';
import { Pressable, StyleSheet, Text, View } from 'react-native';
import { useTranslation } from 'react-i18next';

import { Fonts, Radius, Spacing } from '@/constants/theme';
import { useTheme } from '@/hooks/use-theme';

import type { CardCarouselMoreTile } from './card-carousel.types';

// The trailing "see more" tile of a CardCarousel — shared by the web and
// native variants. Card-width (240px) when the row overflows; flexGrow lets
// it absorb the leftover space when it doesn't.
export function MoreTile({ title, description, href }: CardCarouselMoreTile) {
  const theme = useTheme();
  const { t } = useTranslation();
  return (
    // Plain-object style, pressed feedback via children — see the note in
    // events-carousel.web.tsx on <Link asChild>'s style merge.
    <Link href={href} asChild>
      <Pressable
        accessibilityRole="link"
        style={StyleSheet.flatten([
          styles.tile,
          { backgroundColor: theme.accent, borderColor: theme.border },
        ])}>
        {({ pressed }) => (
          <View style={[styles.inner, pressed && { opacity: 0.7 }]}>
            <Text style={[styles.title, { color: theme.text, fontFamily: Fonts?.serif }]}>
              {title}
            </Text>
            {description ? (
              <Text style={[styles.description, { color: theme.mutedForeground }]}>
                {description}
              </Text>
            ) : null}
            <View style={styles.cta}>
              <Text style={[styles.ctaText, { color: theme.accentForeground }]}>
                {t('common.seeAll')}
              </Text>
              <ArrowRight size={16} color={theme.accentForeground} />
            </View>
          </View>
        )}
      </Pressable>
    </Link>
  );
}

const styles = StyleSheet.create({
  tile: {
    flexGrow: 1,
    flexShrink: 0,
    flexBasis: 240,
    minWidth: 240,
    borderRadius: Radius.xl,
    borderWidth: 1,
    justifyContent: 'center',
  },
  inner: {
    maxWidth: 420,
    padding: Spacing.four,
    gap: Spacing.two,
  },
  title: {
    fontSize: 20,
    lineHeight: 26,
    fontWeight: '700',
  },
  description: {
    fontSize: 14,
    lineHeight: 21,
  },
  cta: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    marginTop: Spacing.one,
  },
  ctaText: {
    fontSize: 14,
    fontWeight: '600',
  },
});
